import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Send } from 'lucide-react';
import { connectIntegration, integrationsEnabled } from '../../lib/integrations';
import { getTicketDestinations, getTicketProjects, getTicketIssueTypes, getTicketFields, publishTicket } from '../../services/ticketWriter';

export default function TicketDestination({ session, onAttempt, onPublished, onBusy, disabled }) {
  const [destinations, setDestinations] = useState([]);
  const [provider, setProvider] = useState('');
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState('');
  const [types, setTypes] = useState([]);
  const [issueType, setIssueType] = useState('');
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState('destinations');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const mounted = useRef(true);
  const sending = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const destination = destinations.find(d => d.id === provider);
  const site = destination?.site_id;
  const writable = destination?.canPublish;

  useEffect(() => {
    let active = true;
    setLoading('destinations'); setError(''); setDestinations([]); setProvider(''); setProject('');
    getTicketDestinations().then(rows => {
      if (!active) return;
      setDestinations(rows);
      setProvider(rows.find(d => d.canPublish)?.id || rows[0]?.id || '');
    }).catch(e => { if (active) setError(e.message); }).finally(() => { if (active) setLoading(''); });
    return () => { active = false; };
  }, [revision]);

  useEffect(() => {
    if (!writable || !site) return;
    const controller = new AbortController(); let active = true;
    setProjects([]); setProject(''); setLoading('projects'); setError('');
    getTicketProjects(provider, site, controller.signal).then(result => { if (active) setProjects(result.projects); })
      .catch(e => { if (active) setError(e.message); }).finally(() => { if (active) setLoading(''); });
    return () => { active = false; controller.abort(); };
  }, [provider, site, writable, revision]);

  useEffect(() => {
    setTypes([]); setIssueType(''); setFields([]); setValues({});
    if (!project || !writable) return;
    const controller = new AbortController(); let active = true;
    setLoading('types'); setError('');
    getTicketIssueTypes(provider, site, project, controller.signal).then(result => { if (active) setTypes(result.issueTypes); })
      .catch(e => { if (active) setError(e.message); }).finally(() => { if (active) setLoading(''); });
    return () => { active = false; controller.abort(); };
  }, [provider, site, project, writable]);

  useEffect(() => {
    setFields([]); setValues({});
    if (!issueType) return;
    const controller = new AbortController(); let active = true;
    setLoading('fields'); setError('');
    getTicketFields(provider, site, project, issueType, controller.signal).then(result => { if (active) setFields(result.fields); })
      .catch(e => { if (active) { setError(e.message); setIssueType(''); } }).finally(() => { if (active) setLoading(''); });
    return () => { active = false; controller.abort(); };
  }, [provider, site, project, issueType]);

  const connect = async () => {
    setBusy(true); setError('');
    try { await connectIntegration(provider); if (mounted.current) setRevision(r => r + 1); }
    catch (e) { if (mounted.current) setError(e.message); }
    finally { if (mounted.current) setBusy(false); }
  };
  const send = async event => {
    event.preventDefault();
    if (sending.current || disabled) return;
    sending.current = true; setBusy(true); onBusy(true); setError('');
    const attempt = session.publishAttempt || { provider, body: {
      request_id: session.id, site_id: site, project_id: project, issue_type_id: issueType,
      title: session.draft.title, description: session.draft.description, fields: values,
    } };
    try {
      // Save the exact request before the side effect so retries after a reload
      // use the same content and idempotency key.
      onAttempt(attempt);
      const result = await publishTicket(attempt.provider, attempt.body);
      if (mounted.current) onPublished(result);
    } catch (e) {
      if (mounted.current) {
        // 409 can mean the request is in progress; keep its original payload.
        if (e.status >= 400 && e.status < 500 && e.status !== 408 && !e.publishingUncertain) onAttempt(null);
        setError(e.message);
      }
    } finally { sending.current = false; if (mounted.current) { setBusy(false); onBusy(false); } }
  };

  if (session.published) return <section className="v2-panel ticket-published" aria-label="Published ticket">
    <h2>Ticket created</h2><p>Your reviewed draft was sent successfully.</p>
    <a className="v2-button secondary" href={session.published.url} target="_blank" rel="noopener noreferrer">Open {session.published.key}<ExternalLink size={16}/></a>
  </section>;

  return <section className="v2-panel ticket-destination" aria-label="Send to an integrated system">
    <h2>Send to an integrated system</h2>
    <p>Choose where this reviewed draft should become a ticket.</p>
    {!integrationsEnabled ? <p>Publishing requires Firebase sign-in and a connected system. You can still copy or download your draft.</p> : <>
      {session.publishAttempt ? <form onSubmit={send}>
        <p>A publishing attempt is saved for this draft. Check its status to recover the ticket link without sending it twice.</p>
        <button className="v2-button" disabled={busy || disabled}>{busy ? 'Checking…' : 'Check publishing status'}</button>
      </form> : <form onSubmit={send}>
        <fieldset disabled={busy || disabled || !!loading}>
          <label className="v2-field"><span>Destination</span><select value={provider} onChange={e => { setProvider(e.target.value); setProject(''); }} required>
            <option value="">Choose a system</option>{destinations.map(d => <option key={d.id} value={d.id}>{d.name}{d.site_name ? ` · ${d.site_name}` : ''}</option>)}
          </select></label>
          {destination && !writable && <div className="ticket-notice">
            <p>{destination.connected ? 'Reconnect Jira to grant permission to create tickets. Your existing import settings are kept.' : 'Connect Jira to choose a project and create a ticket.'}</p>
            <button className="v2-button secondary" type="button" onClick={connect}>{destination.connected ? 'Reconnect Jira' : 'Connect Jira'}</button>
          </div>}
          {writable && <>
            <div className="v2-form-grid">
              <label className="v2-field"><span>Project</span><select required value={project} onChange={e => setProject(e.target.value)}>
                <option value="">Choose a project</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name} ({p.id})</option>)}
              </select></label>
              <label className="v2-field"><span>Issue type in {destination.name}</span><select required disabled={!project || !types.length} value={issueType} onChange={e => setIssueType(e.target.value)}>
                <option value="">Choose an issue type</option>{types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select></label>
            </div>
            {session.type === 'prd' && <p>A PRD is published as the issue type you choose, with the full requirements in its description.</p>}
            {['epic', 'initiative'].includes(session.type) && <p>Choose the matching type if your Jira project supports it, or select another available type.</p>}
            {!loading && !projects.length && <p>No projects are available to this connection.</p>}
            {project && !loading && !types.length && <p>No issue types are available for creation in this project.</p>}
            {fields.map(field => field.supported ? <label className="v2-field" key={field.id}><span>{field.label} (required by {destination.name})</span>
              {field.options.length ? <select required value={values[field.id] || ''} onChange={e => setValues(v => ({ ...v, [field.id]: e.target.value }))}>
                <option value="">Choose {field.label}</option>{field.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select> : <input required type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'} step={field.type === 'number' ? 'any' : undefined} maxLength={3000} value={values[field.id] || ''} onChange={e => setValues(v => ({ ...v, [field.id]: e.target.value }))}/>}
            </label> : <p key={field.id} className="ticket-notice">{destination.name} requires “{field.label}”, which must be filled in there. Copy your draft to create the ticket in {destination.name}.</p>)}
            <button className="v2-button" disabled={!project || !issueType || fields.some(f => !f.supported) || !session.draft.title.trim() || !session.draft.description.trim()}><Send size={16}/>{busy ? 'Sending…' : `Create ticket in ${destination.name}`}</button>
          </>}
        </fieldset>
      </form>}
      {loading && <p role="status">Loading {loading === 'types' ? 'issue types' : loading}…</p>}
      <div className="v2-actions"><button className="v2-button secondary" type="button" disabled={busy || !!loading || !!session.publishAttempt} onClick={() => setRevision(r => r + 1)}>Refresh destinations</button><Link to="/onboarding">Manage integrations</Link></div>
    </>}
    {error && <p role="alert" className="v2-error">{error}</p>}
  </section>;
}
